import { createAlova } from "alova";
import SolidHook from "alova/solid";
import customFetchAdapter from "./adapter";
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  clearAllTokens,
} from "./auth";

const baseURL = import.meta.env.VITE_URL_SSO;

// ─────────────────────────────────────────────
// Refresh token queue
// ─────────────────────────────────────────────

let isRefreshing = false;
let pendingQueue: (() => void)[] = [];

// ─────────────────────────────────────────────
// Refresh access token
// ─────────────────────────────────────────────

async function refreshAccessToken() {
  const refreshTokenValue = getRefreshToken();

  if (!refreshTokenValue) {
    throw new Error("No refresh token");
  }

  const res = await fetch(`${baseURL}/auth/refresh-token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refresh_token: refreshTokenValue,
    }),
  });

  if (!res.ok) {
    throw new Error("Refresh token failed");
  }

  const data = await res.json();

  setToken(data.data.access_token);
  setRefreshToken(data.data.refresh_token);

  return data.data.access_token;
}

// ─────────────────────────────────────────────
// Retry config
// ─────────────────────────────────────────────

interface RetryableConfig {
  _retry?: boolean;
  headers: Record<string, string>;
  [key: string]: any;
}

// ─────────────────────────────────────────────
// Auth Alova
// ─────────────────────────────────────────────

export const authAlova = createAlova({
  baseURL,
  requestAdapter: customFetchAdapter,
  statesHook: SolidHook,

  responded: {
    onSuccess: (res) => res,
  },
});

// ─────────────────────────────────────────────
// Protected API Alova
// ─────────────────────────────────────────────

export const apiAlova = createAlova({
  baseURL,
  requestAdapter: customFetchAdapter,
  statesHook: SolidHook,

  beforeRequest: (method) => {
    const token = getToken();

    if (token) {
      method.config.headers = {
        ...(method.config.headers || {}),
        Authorization: `Bearer ${token}`,
      };
    }
  },

  responded: {
    onSuccess: (res, meta) => {
      return {
        ...(res as any),
        fromCache: meta.fromCache,
      };
    },

    async onError(err, method) {
      const config = method.config as RetryableConfig;

      // Hanya retry 401 sekali
      if (err.status !== 401 || config._retry) {
        throw err;
      }

      config._retry = true;

      // ───────────────────────────────────────
      // Request lain sedang refresh token
      // ───────────────────────────────────────

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push(async () => {
            try {
              const retryRes = await method.send();
              resolve(retryRes);
            } catch (error) {
              reject(error);
            }
          });
        });
      }

      // ───────────────────────────────────────
      // Refresh token
      // ───────────────────────────────────────

      try {
        isRefreshing = true;

        const newToken = await refreshAccessToken();

        // Retry semua request yang menunggu
        const queue = pendingQueue;
        pendingQueue = [];

        queue.forEach((callback) => callback());

        // Update token untuk request yang pertama kali
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };

        return await method.send();
      } catch (error) {
        pendingQueue = [];

        clearAllTokens();

        window.location.href = "/";

        throw error;
      } finally {
        isRefreshing = false;
      }
    },
  },
});

// ─────────────────────────────────────────────
// Cache invalidation
// ─────────────────────────────────────────────

// Global flag to force refresh on list pages
let globalRefreshFlag = 0;

export function clearCache() {
  globalRefreshFlag++;
}

export function getGlobalRefreshFlag() {
  return globalRefreshFlag;
}
